'use client';

import { jwtDecode } from 'jwt-decode';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { useCallback, useEffect, useState } from 'react';
import type { UnpackedUserToken } from '@/app_actions/Account/entities';
import { CookieService } from '@/app_lib/CookieService';
import { useUser } from '@/app_lib/providers/UserProvider';
import { ONE_TRUST_AUTO_BLOCK, ONE_TRUST_DOMAIN_ID, type OneTrustUser } from '@/app_lib/services/oneTrust/service';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { generateOneTrustJwtToken } from '@/app_lib/utils/jwtUtils';

const customOneTrustLocation = (query: string) => {
  let geolocationByFeatureFlag: { countryCode: string; stateCode: string } | undefined;
  switch (query) {
    case 'EU': // any country in europe, default: users opted out, ads don't show, one trust banner shows
      geolocationByFeatureFlag = {
        countryCode: 'FR',
        stateCode: 'IDF',
      };
      break;
    case 'USA': // VA, CT, CO, UT, NV, TX, OR, states in US w cookie consent laws, default: user sees ads but banner shows
      geolocationByFeatureFlag = {
        countryCode: 'US',
        stateCode: 'VA',
      };
      break;
    case 'CAN': // Canada w/o Quebec, default: users see ads and banner
      geolocationByFeatureFlag = {
        countryCode: 'CA',
        stateCode: 'BC',
      };
      break;
    case 'CPRA': // California, default: user sees ads and banner
      geolocationByFeatureFlag = {
        countryCode: 'US',
        stateCode: 'CA',
      };
      break;
    case 'GDPR': // China and Quebec, default: users don't see ads and banner shows up
      geolocationByFeatureFlag = {
        countryCode: 'CA',
        stateCode: 'QC',
      };
      break;
    case 'GLOBAL': // global audience, default: users are opted out, ads show, no one trust banner
      geolocationByFeatureFlag = {
        countryCode: 'US',
        stateCode: 'NY',
      };
      break;
    default:
      geolocationByFeatureFlag = undefined;
      break;
  }

  return geolocationByFeatureFlag;
};

// NDR (National Debt Relief) page paths where OneTrust should be disabled
const NDR_GOOGLE_PAGE_PREFIX = '/reviews-1'; // Google organic traffic version
const NDR_BING_PAGE_PREFIX = '/reviews-2'; // Bing organic traffic version
const OneTrustScriptSrc = ONE_TRUST_AUTO_BLOCK
  ? `https://cdn.cookielaw.org/consent/${ONE_TRUST_DOMAIN_ID}/OtAutoBlock.js`
  : `https://cdn.cookielaw.org/consent/${ONE_TRUST_DOMAIN_ID}/otSDKStub.js`;
export const OneTrustScript = () => {
  const pathname = usePathname();
  const { setHasConsent, setPersonalizedAds, setPerformanceTracking } = useUser();
  const searchParams = useSearchParams();
  const [otUser, setOtUser] = useState<OneTrustUser | null>(null);

  // Disable OneTrust on NDR pages (including all subpages)
  const isNdrPage =
    (pathname ?? '').startsWith(NDR_GOOGLE_PAGE_PREFIX) || (pathname ?? '').startsWith(NDR_BING_PAGE_PREFIX);

  const handleCustomGeolocationQuery = useCallback(() => {
    const customGeolocation = searchParams?.get('customGeolocation') || '';
    CookieService.set('customGeolocation', customGeolocation);
    const geolocationByQuery = customOneTrustLocation(customGeolocation);
    setOtUser({
      dataSubjectParams: {
        isAnonymous: true,
      },
      geolocationResponse: geolocationByQuery,
    });
  }, [searchParams]);

  const handleCustomGeolocationCookie = useCallback(() => {
    const geolocationByCookie = customOneTrustLocation(CookieService.get('customGeolocation') as string);
    setOtUser({
      dataSubjectParams: {
        isAnonymous: true,
      },
      geolocationResponse: geolocationByCookie,
    });
  }, []);

  useEffect(() => {
    // Skip OneTrust setup on NDR pages

    const setUser = async () => {
      const pianoToken = CookieService.get('__utp');

      if (pianoToken) {
        const decoded = jwtDecode(pianoToken) as UnpackedUserToken;
        const pianoUid = decoded?.sub || '';

        const sessionUser = JSON.parse(sessionStorage.getItem('otSessionUser') || '{}');

        if (sessionUser && pianoUid === sessionUser.dataSubjectParams?.id) {
          setOtUser(sessionUser);
          return;
        }

        const token = await generateOneTrustJwtToken(pianoUid); // will return an empty string if pianoUid is falsy

        if (token) {
          const user = {
            dataSubjectParams: {
              id: pianoUid,
              isAnonymous: false,
              token,
            },
          };
          sessionStorage.setItem('otSessionUser', JSON.stringify(user));
          setOtUser(user);
          return;
        }
      } else if (process.env.ENVIRONMENT === 'qa' && searchParams?.get('customGeolocation')) {
        handleCustomGeolocationQuery();
        return;
      } else if (process.env.ENVIRONMENT === 'qa' && CookieService.get('customGeolocation')) {
        handleCustomGeolocationCookie();
        return;
      }
      setOtUser({ dataSubjectParams: { isAnonymous: true } });
    };

    setUser();
  }, [searchParams, handleCustomGeolocationCookie, handleCustomGeolocationQuery, isNdrPage]);

  useEffect(() => {
    // Skip OneTrust wrapper on NDR pages

    window.OptanonWrapper = () => {
      const requiredFlags = ['C0001'];
      const performanceFlags = ['C0002', 'C0003'];
      const personalizationFlags = ['C0004', 'C0005'];
      const activeGroups = window.OnetrustActiveGroups?.split(',') || [];
      const gpcSignal =
        typeof navigator !== 'undefined' &&
        (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

      if (activeGroups.length > 0) {
        const hasRequiredFlags = requiredFlags.every((flag) => activeGroups.includes(flag));
        const hasPersonalizedAdsFlags = gpcSignal
          ? false
          : personalizationFlags.every((flag) => activeGroups.includes(flag));
        const hasPerformanceFlags = performanceFlags.every((flag) => activeGroups.includes(flag));
        setHasConsent(hasRequiredFlags);
        setPerformanceTracking(hasPerformanceFlags);
        setPersonalizedAds(hasPersonalizedAdsFlags);

        if (typeof window.gtag === 'function') {
          window.gtag('consent', 'update', {
            // biome-ignore lint/style/useNamingConvention: onetrust uses snake_case
            ad_personalization: hasPersonalizedAdsFlags ? 'granted' : 'denied',
            // biome-ignore lint/style/useNamingConvention: onetrust uses snake_case
            ad_storage: hasPersonalizedAdsFlags ? 'granted' : 'denied',
            // biome-ignore lint/style/useNamingConvention: onetrust uses snake_case
            ad_user_data: hasPersonalizedAdsFlags ? 'granted' : 'denied',
            // biome-ignore lint/style/useNamingConvention: onetrust uses snake_case
            analytics_storage: hasPerformanceFlags ? 'granted' : 'denied',
            // biome-ignore lint/style/useNamingConvention: onetrust uses snake_case
            functionality_storage: activeGroups.includes('C0003') ? 'granted' : 'denied',
            // biome-ignore lint/style/useNamingConvention: onetrust uses snake_case
            personalization_storage: hasPersonalizedAdsFlags ? 'granted' : 'denied',
          });
        }
      }
    };
    window.OptanonWrapper();
  }, [setHasConsent, setPersonalizedAds, setPerformanceTracking, isNdrPage]);

  // TODO: Do we want to bring back that watchdog timer?

  return (
    <>
      {otUser && (
        <Script
          data-cy={ContentPlacement.OneTrustUser}
          id='OneTrustUserObject'
          strategy='beforeInteractive'
          type='text/javascript'
        >
          var OneTrust = {JSON.stringify(otUser)}
        </Script>
      )}
      <Script
        async
        data-cy={ContentPlacement.OneTrustScript}
        data-document-language='true'
        data-domain-script={ONE_TRUST_AUTO_BLOCK ? '' : ONE_TRUST_DOMAIN_ID}
        src={OneTrustScriptSrc}
        strategy='beforeInteractive'
        type='text/javascript'
      />
      {ONE_TRUST_AUTO_BLOCK && (
        <Script
          async
          data-document-language='true'
          data-domain-script={ONE_TRUST_DOMAIN_ID}
          src={`https://cdn.cookielaw.org/consent/${ONE_TRUST_DOMAIN_ID}/otSDKStub.js`}
          strategy='beforeInteractive'
          type='text/javascript'
        />
      )}

      <Script
        ccpa-opt-out-geo='us'
        ccpa-opt-out-ids='C0004'
        ccpa-opt-out-lspa='false'
        src='https://cdn.cookielaw.org/opt-out/otCCPAiab.js'
        strategy='beforeInteractive'
        type='text/javascript'
      />
    </>
  );
};
